import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import type { TableColumn } from "@/types/component";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import type { SentSmsListItem, SendSmsPayload } from "../types/sms-broadcast.types";
import { smsBroadcastApi } from "../services/sms-broadcast.api";
import SendSmsModal from "../components/SendSmsModal";
import styles from "./SmsBroadcastPage.module.scss";

const SmsBroadcastPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canSend = hasPermission(PERMISSIONS.SMS_BROADCAST_SEND);

  const [rows, setRows] = useState<SentSmsListItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    const tmr = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(tmr);
  }, [searchTerm]);

  const fetchList = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await smsBroadcastApi.getSentList({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearch.trim() || undefined,
      });
      setRows(res.data);
      setTotalRecords(res.total);
    } catch {
      setRows([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearch]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleSendSubmit = async (payload: SendSmsPayload) => {
    await smsBroadcastApi.send(payload);
    toast.success(
      t("smsBroadcast.sendSuccess"),
    );
    await fetchList();
  };

  const columns: TableColumn<SentSmsListItem>[] = [
    {
      header: t("smsBroadcast.columns.sentAt"),
      accessor: (row) => row.sentAt,
      className: styles.dateColumn,
    },
    {
      header: t("smsBroadcast.columns.recipients"),
      accessor: (row) => (
        <span className={styles.messageCell} title={row.recipientSummary}>
          {row.recipientSummary}
        </span>
      ),
      className: styles.recipientColumn,
    },
    {
      header: t("smsBroadcast.columns.message"),
      accessor: (row) => (
        <span className={styles.messageCell} title={row.messagePreview}>
          {row.messagePreview}
        </span>
      ),
      className: styles.messageColumn,
    },
    {
      header: t("smsBroadcast.columns.status"),
      accessor: (row) => row.status ?? "—",
      className: styles.statusColumn,
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("smsBroadcast.title")} />
      <PageHeader
        searchPlaceholder={t("smsBroadcast.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={setSearchTerm}
        onAddNew={canSend ? () => setIsSendModalOpen(true) : undefined}
        addButtonLabel={
          canSend
            ? t("smsBroadcast.sendNewButton")
            : undefined
        }
      />
      <Table
        columns={columns}
        data={rows}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        emptyMessage={t("smsBroadcast.noData")}
      />
      <SendSmsModal
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        onSubmit={handleSendSubmit}
      />
    </DashboardLayout>
  );
};

export default SmsBroadcastPage;
